// Backend API client. Wraps fetch with JWT auth + JSON handling.
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
const TOKEN_KEY = 'crm_jwt';

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  data: any;
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  // Skip JSON body serialization (for FormData / external fetches)
  raw?: boolean;
}

function buildQuery(query?: RequestOptions['query']): string {
  if (!query) return '';
  const entries = Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
}

export async function api<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${API_URL}${path}${buildQuery(opts.query)}`;
  const token = getToken();

  const headers: Record<string, string> = { ...(opts.headers || {}) };
  if (token && !headers['Authorization']) headers['Authorization'] = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.raw) {
      body = opts.body;
    } else {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(url, {
    method: opts.method || 'GET',
    headers,
    body,
  });

  let data: any = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    data = await res.json().catch(() => null);
  } else {
    data = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const msg = (data && data.message) || res.statusText || 'Request failed';
    throw new ApiError(msg, res.status, data);
  }

  return data as T;
}
